// Top-of-Overview "what do I do today?" card. Shows up to 3 ranked priorities
// — pulled from today's briefing when available, synthesized from live alerts
// otherwise. Never shows raw weather data; this is the answer screen.

import { useEffect, useState } from 'react';
import { AlertTriangle, Droplets, Bug, Cloud, Satellite, Sprout, Wrench, Target } from 'lucide-react';
import { DataCard } from './ui/data-card';
import { Badge, type BadgeProps } from './ui/badge';
import { getTodayPriorities, type PriorityItem } from '../api/overview.js';
import { cn } from '../lib/utils';

interface Props {
  courseSlug: string;
}

const PRIORITY_VARIANT: Record<PriorityItem['priority'], BadgeProps['variant']> = {
  P1: 'destructive',
  P2: 'warning',
  P3: 'default',
};

const CATEGORY_ICON: Record<string, typeof AlertTriangle> = {
  sensor: Droplets,
  disease: Bug,
  irrigation: Droplets,
  weather: Cloud,
  satellite: Satellite,
  fertility: Sprout,
  scouting: Target,
  operations: Wrench,
};

export function TodayPrioritiesCard({ courseSlug }: Props) {
  const [priorities, setPriorities] = useState<PriorityItem[] | null>(null);
  const [source, setSource] = useState<'briefing' | 'synthesized' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await getTodayPriorities(courseSlug);
        if (cancelled) return;
        setPriorities(resp.priorities);
        setSource(resp.source);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [courseSlug]);

  return (
    <DataCard
      glow
      className="mt-6"
      title="Today's priorities"
      subtitle={
        source === 'briefing'
          ? 'From this morning’s AI briefing · ranked by urgency'
          : source === 'synthesized'
            ? 'Live from active alerts · AI briefing will supersede'
            : undefined
      }
    >
      {error && (
        <div className="rounded-md border border-[color:var(--color-error)]/30 bg-error-muted p-3 text-xs text-fg">
          {error}
        </div>
      )}
      {!error && priorities == null && (
        <div className="text-sm text-fg-muted">Loading…</div>
      )}
      {!error && priorities != null && priorities.length === 0 && (
        <div className="rounded-md border border-dashed border-border p-4 text-center text-sm text-fg-muted">
          All clear. No P1/P2 signals today.
        </div>
      )}
      {!error && priorities != null && priorities.length > 0 && (
        <ol className="space-y-3">
          {priorities.map((p, idx) => {
            const Icon = CATEGORY_ICON[p.category] ?? AlertTriangle;
            return (
              <li
                key={p.id}
                className={cn(
                  'group relative flex gap-3 rounded-md border border-border bg-bg-subtle p-4',
                  p.priority === 'P1' && 'border-[color:var(--color-error)]/40',
                )}
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-bg-card text-greeniq-400">
                  <Icon size={16} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={PRIORITY_VARIANT[p.priority]}>{p.priority}</Badge>
                    <span className="text-[10px] uppercase tracking-wide text-fg-muted">
                      {p.category}
                    </span>
                    <span className="text-[10px] text-fg-muted">· {idx + 1} of {priorities.length}</span>
                  </div>
                  <div className="mt-1 font-display text-sm font-semibold text-fg">
                    {p.title}
                  </div>
                  <div className="mt-0.5 text-sm text-fg-secondary">{p.detail}</div>
                  {p.action && (
                    <div className="mt-2 text-xs text-greeniq-400">→ {p.action}</div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </DataCard>
  );
}
